import { KalturaAPIException } from '@kaltura-ott/ott-client-types/api';
import { KalturaLanguageListResponse, OttUserAnonymousLoginActionArgs } from '@kaltura-ott/ott-client-types/api/types';
import {
  anonymousLogin,
  ConnectivityType,
  deviceBrandGetList,
  getOAuthToken,
  getServicesTokenByUsernameAndPassword,
  HouseholdDevice,
  householdGetDevice,
  householdRemoveDevice,
  HouseholdStreamingDevice,
  KuxConfig,
  KuxDms,
  KuxError,
  kuxUpdateConfig,
  languageList,
  logoutOttUser,
  MACType,
  OAuthGetTokenParams,
  PinStructure,
  PINType,
  revokeSession,
  userAppTokenAdd,
  userAppTokenDelete,
  userAppTokenStartSession,
  userLoginWithExtraParams,
  userLoginWithPin,
} from '@kaltura-ott/tvpil';
import { lastValueFrom } from 'rxjs';
import sha256 from 'sha256';

import { AppParams } from '../app/app-params';
import { getNetworkMode } from '../category/playout/playout-store.service';

export const MIGRATION_SECRET = 'bZxgm5ISBubawiB0ucbKYq1ZIRv1tkiB';
export const SILENT_LOGIN_SECRET = "rc<k'HJqe*22rEG~Y2=+bx:wk_%mnh";
export const USERNAME_PASSWORD_SERVICE_URL = 'http://mobilniAplikace.o2.cz';

export const anonymousUserLogin = async (data: OttUserAnonymousLoginActionArgs) => {
  try {
    const login = await lastValueFrom(
      anonymousLogin({
        ...data,
      }),
    );
    kuxUpdateConfig({ ks: login.ks } as KuxConfig);
    return login.ks;
  } catch (e) {
    console.log('retry');
    console.trace(e);
    throw e;
  }
};

export const oAuthLogin = async (tokenParams: OAuthGetTokenParams, loginArgs: OttUserAnonymousLoginActionArgs) => {
  let tokenData;
  try {
    tokenData = await lastValueFrom(getOAuthToken(tokenParams));
  } catch (e) {
    return e;
  }

  const response = await lastValueFrom(
    userLoginWithExtraParams(
      {
        ...loginArgs,
        password: '11111',
        username: '11111',
      },
      [
        { key: 'loginType', value: 'accessToken' },
        { key: 'accessToken', value: tokenData.accessToken },
      ],
    ),
  );

  if (response instanceof Error) {
    throw response;
  }

  kuxUpdateConfig({ ks: response.loginSession.ks } as KuxConfig);

  return {
    accessToken: tokenData.accessToken,
    refreshToken: tokenData.refreshToken,
    ks: response.loginSession.ks,
    userData: response.user,
  };
};

export interface UsernamePasswordLoginAgs {
  username: string;
  password: string;
  udid: string;
  O2AuthServiceParam: string;
  digSign?: string;
  currentDate?: string;
  clientId?: string;
}

export const usernamePasswordLogin = async ({
  username,
  password,
  udid,
  O2AuthServiceParam,
  digSign,
  currentDate,
  clientId,
}: UsernamePasswordLoginAgs) => {
  const response = await lastValueFrom(
    getServicesTokenByUsernameAndPassword(
      username,
      password,
      udid,
      O2AuthServiceParam ?? USERNAME_PASSWORD_SERVICE_URL,
      digSign,
      currentDate,
      clientId,
    ),
  );

  if (response instanceof Error) {
    throw response;
  }

  if (!response || !response.jwt) {
    throw new Error('tvpillError.somethingWrong');
  }

  return {
    token: response,
  };
};

/**
 * Login to the backend with third-party JWT token
 *
 * @param loginArgs
 * @param token
 * @param brandId
 * @param uidi
 * @param serviceId
 */
export const jwtTokenLogin = async (
  loginArgs: OttUserAnonymousLoginActionArgs,
  token: string,
  brandId: number,
  uidi: string,
  serviceId: string,
) => {
  const login = await lastValueFrom(
    userLoginWithExtraParams(
      {
        ...loginArgs,
        password: 'NONE',
        username: 'NONE',
      },
      [
        { key: 'token', value: token },
        { key: 'loginType', value: 'accessToken' },
        { key: 'brandId', value: brandId.toString() },
        { key: 'externalId', value: serviceId },
      ],
    ),
  );

  if (login instanceof KalturaAPIException) {
    throw new KuxError(login.message, login.code);
  }

  if (login instanceof Error) {
    throw login;
  }

  kuxUpdateConfig({ ks: login.loginSession.ks } as KuxConfig);

  return {
    ks: login.loginSession.ks,
    userData: login.user,
  };
};

export const generateSilentPin = (udid?: string) => {
  if (!udid) {
    return '';
  }
  return sha256(`${udid.replace(/:/g, '').toUpperCase()}|${SILENT_LOGIN_SECRET}`);
};

/**
 * Generate PIN code for migration purposes
 *
 * @param mac
 */
export const generateMigrationPin = (mac?: string) => {
  if (!mac) {
    return '';
  }
  return sha256(`${mac.replace(/:/g, '').toUpperCase()}|${MIGRATION_SECRET}`);
};

// for backward compatibility
const getOldSilentPin = (loginArgs: OttUserAnonymousLoginActionArgs): string => {
  let pin = '';
  switch (loginArgs.partnerId) {
    case 3201:
      pin = generateMigrationPin(loginArgs.udid);
      break;
    default:
      pin = generateSilentPin(loginArgs.udid);
  }
  return pin;
};

const createPinByStructure = (pinValue: string, silentLoginSettings: KuxDms['silentLoginSettings']): string => {
  let result = '';
  silentLoginSettings.structure?.forEach((value) => {
    switch (value) {
      case PinStructure.PIN:
        result += pinValue;
        break;
      case PinStructure.Pipe:
        result += '|';
        break;
      case PinStructure.Secret:
        result += silentLoginSettings.loginTypeID;
        break;
      default:
    }
  });
  return sha256(result);
};

const getPin = async (
  loginArgs: OttUserAnonymousLoginActionArgs,
  pinType: PINType,
  silentLoginSettings: KuxDms['silentLoginSettings'],
  silentLoginParams: AppParams['silentLoginParams'],
) => {
  if (pinType === PINType.SN) {
    if (!silentLoginSettings.loginTypeID || !silentLoginParams?.serialNumber) {
      return getOldSilentPin(loginArgs);
    }
    return createPinByStructure(silentLoginParams?.serialNumber, silentLoginSettings);
  }
  if (pinType === PINType.IP && silentLoginSettings.networkModeURL) {
    if (!silentLoginParams?.ip) {
      console.warn('IP for silent login is not defined');
      return '';
    }

    const networkMode = await getNetworkMode(silentLoginSettings.networkModeURL);
    if (networkMode !== ConnectivityType.iptv) {
      console.warn("Network mode isn't OTT");
      return '';
    }
    return createPinByStructure(silentLoginParams?.ip, silentLoginSettings);
  }

  if (pinType === PINType.MAC) {
    if (silentLoginSettings.macType === MACType.LAN) {
      if (!silentLoginParams?.macLan) {
        console.warn('macLAN for silent login is not defined');
        return '';
      }
      return createPinByStructure(silentLoginParams?.macLan, silentLoginSettings);
    } else {
      if (!silentLoginParams?.macWiFi) {
        console.warn('macWIFI for silent login is not defined');
        return '';
      }
      return createPinByStructure(silentLoginParams?.macWiFi, silentLoginSettings);
    }
  }

  if (pinType === PINType.UDID) {
    if (!silentLoginParams?.UDID) {
      console.warn('UDID is not defined');
      return '';
    }
    return createPinByStructure(silentLoginParams?.UDID, silentLoginSettings);
  }

  if (pinType === PINType.WEBSLID) {
    if (!silentLoginParams?.WEBSLID) {
      console.warn('WEBSLID is not defined');
      return '';
    }
    return createPinByStructure(silentLoginParams?.WEBSLID, silentLoginSettings);
  }

  return '';
};

/**
 *  Logs in using a device's unique identifier
 * @param loginArgs
 * @param silentLoginSettings
 * @param silentLoginParams
 */
export const silentLogin = async (
  loginArgs: OttUserAnonymousLoginActionArgs,
  silentLoginSettings: KuxDms['silentLoginSettings'],
  silentLoginParams: AppParams['silentLoginParams'],
) => {
  for (const pinType of silentLoginSettings.pinType || []) {
    try {
      const pin = await getPin(loginArgs, pinType, silentLoginSettings, silentLoginParams);
      if (!pin) {
        throw new Error('no pin in silentLogin');
      }
      const login = await lastValueFrom(
        userLoginWithPin({
          ...loginArgs,
          pin,
        }),
      );

      if (!login?.loginSession?.ks) {
        throw new Error('No ks obtained in userLoginWithPin');
      }

      kuxUpdateConfig({ ks: login.loginSession.ks } as KuxConfig);

      return {
        ks: login.loginSession.ks,
      };
    } catch (e) {
      console.warn('Silent login error', e);
    }
  }
  return false;
};

export const pinLogin = async (accessToken: string, loginArgs: OttUserAnonymousLoginActionArgs) => {
  const login = await lastValueFrom(
    userLoginWithExtraParams(
      {
        ...loginArgs,
        password: '11111',
        username: '11111',
      },
      [
        { key: 'loginType', value: 'loginWithPinOauth2' },
        { key: 'accessToken', value: accessToken },
      ],
    ),
  );

  if (login instanceof KalturaAPIException) {
    throw new KuxError(login.message, login.code);
  }

  if (login instanceof Error) {
    throw login;
  }

  kuxUpdateConfig({ ks: login.loginSession.ks } as KuxConfig);
  return {
    ks: login.loginSession.ks,
    username: login.user.username,
  };
};

export const getHouseholdDevice = () => lastValueFrom(householdGetDevice());

//:TODO: should be deleted when the platforms was implement a new BL for languages
export const getLanguageList = async () => {
  const { objects }: KalturaLanguageListResponse = await lastValueFrom(languageList());
  // const defaultLanguage = objects.find(lang => lang.isDefault);
  // kuxUpdateConfig({ language: defaultLanguage.code } as KuxConfig);
  return objects;
};

export const removeHouseholdDevice = (udid: string) => lastValueFrom(householdRemoveDevice(udid));

export const appTokenAdd = () => lastValueFrom(userAppTokenAdd());

export const appTokenDelete = (id: string) => lastValueFrom(userAppTokenDelete(id));

export const sessionRevoke = () => lastValueFrom(revokeSession());

export const ottUserLogout = () => lastValueFrom(logoutOttUser());

export const appTokenStartSession = (params: { id: string; tokenHash: string; udid: string }) =>
  lastValueFrom(userAppTokenStartSession(params));

export const addBrandToDevices = async (devices: HouseholdStreamingDevice[]): Promise<HouseholdDevice[]> => {
  const deviceBrands = await lastValueFrom(deviceBrandGetList());

  return devices
    .map((device) => {
      const deviceBrandName = deviceBrands.find(({ id }) => id === +device.brandId)?.name;
      return {
        ...device,
        activatedOn: device.activatedOn && device.activatedOn * 1000,
        lastActivityTime: device.lastActivityTime && device.lastActivityTime * 1000,
        deviceBrandName,
        name: !device.name || device.name === device.udid ? deviceBrandName : device.name,
      } as HouseholdDevice;
    })
    .sort((a, b) => Number(b.isStreaming) - Number(a.isStreaming) || b.lastActivityTime - a.lastActivityTime);
};
